import { AfterViewInit, Component, ElementRef, HostListener, OnChanges, ViewChild } from '@angular/core';
import { buildTopMenuVars } from './top-menu-theme';
import { TopMenuBindings } from './top-menu-bindings';
import { TopMenuEvent } from './top-menu-events.model';
import { IcSearchSuggestionsComponent } from './ic-search-suggestions/ic-search-suggestions.component';
import { SUGGEST_MIN_LENGTH } from './search-suggestions.service';
import {
  canHover, HOVER_CLICK_GRACE_MS, LIVE_TEXT_MIN_WIDTH, liveEventOf, panelEventsOf, soonEventsOf, TopMenuUnderline, underlineFor, whenIdle,
} from './top-menu-view';

/**
 * New-design network top menu (environment.enableNewTopMenu).
 * View only: every action is emitted and handled by IcHeaderComponent's existing methods.
 */
@Component({
  selector: 'app-ic-top-menu',
  templateUrl: './ic-top-menu.component.html',
  styleUrls: ['./ic-top-menu.component.scss'],
})
export class IcTopMenuComponent extends TopMenuBindings implements OnChanges, AfterViewInit {
  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;
  @ViewChild(IcSearchSuggestionsComponent, { static: false }) suggestionList: IcSearchSuggestionsComponent;

  vars: { [name: string]: string } = buildTopMenuVars({});
  marketplaceOpen = false;
  currencyOpen = false;
  phoneToolsOpen = false;
  searchPanelOpen = false;
  currencySearch = '';
  underline: TopMenuUnderline = { left: 0, width: 0, on: false };
  private eventsRequested = false;
  private hoverOpenedAt = 0;

  constructor(private host: ElementRef<HTMLElement>) {
    super();
  }

  ngOnChanges(): void {
    this.vars = buildTopMenuVars(this.settingsData);
  }

  ngAfterViewInit(): void {
    if (window.innerWidth >= LIVE_TEXT_MIN_WIDTH) {
      whenIdle(() => this.requestEvents());
    }
  }

  get liveEvent(): TopMenuEvent | undefined {
    return liveEventOf(this.events);
  }

  get panelEvents(): TopMenuEvent[] {
    return panelEventsOf(this.events);
  }

  get soonEvents(): TopMenuEvent[] {
    return soonEventsOf(this.events);
  }

  get showSuggestions(): boolean { return !this.isPreview && (this.searchText || '').trim().length >= SUGGEST_MIN_LENGTH; }

  get shortCurrency(): string {
    return (this.currency || '').slice(0, 4);
  }

  onSearchInput(value: string): void {
    this.searchText = value;
    this.searchPanelOpen = true;
    this.searchTextChange.emit(value);
  }

  submitSearch(): void {
    if (!this.disableAccess && !this.isPreview) {
      this.searchPanelOpen = false;
      this.search.emit();
    }
  }

  openSearchPanel(): void {
    this.searchPanelOpen = true;
    this.marketplaceOpen = false;
    this.currencyOpen = false;
    this.requestEvents();
  }

  pickRecent(term: string): void {
    this.onSearchInput(term);
    this.submitSearch();
  }

  pickEvent(event: TopMenuEvent): void {
    if (!this.isPreview) {
      this.setMarketplace(false);
      this.searchPanelOpen = false;
      this.openEvent.emit(event);
    }
  }

  /** A click right after hover opened the panel keeps it open instead of closing it again. */
  toggleMarketplace(link?: HTMLElement): void {
    const justHovered = this.marketplaceOpen && Date.now() - this.hoverOpenedAt < HOVER_CLICK_GRACE_MS;
    this.setMarketplace(justHovered || !this.marketplaceOpen, link);
  }

  /** Hover opens the panel only on devices with a real pointer; touch uses the click. */
  hoverMarketplace(link: HTMLElement): void {
    this.moveUnderline(link);
    if (canHover() && !this.marketplaceOpen) {
      this.hoverOpenedAt = Date.now();
      this.setMarketplace(true, link);
    }
  }

  leaveMarketplace(): void {
    if (canHover() && this.marketplaceOpen) {
      this.setMarketplace(false);
    }
  }

  pickMarketplace(type: 'market' | 'event'): void {
    this.setMarketplace(false);
    this.marketplaceSelect.emit(type);
  }

  moveUnderline(link: HTMLElement): void {
    this.underline = underlineFor(link);
  }

  hideUnderline(): void {
    this.underline = { ...this.underline, on: this.marketplaceOpen };
  }

  toggleCurrency(): void {
    this.currencySearch = '';
    this.currencyOpen = !this.currencyOpen;
    this.marketplaceOpen = false;
  }

  pickCurrency(value: string): void {
    this.currencyOpen = false;
    this.currencyChange.emit(value);
  }

  togglePhoneTools(): void {
    this.phoneToolsOpen = !this.phoneToolsOpen;
  }

  private setMarketplace(open: boolean, link?: HTMLElement): void {
    if (open && this.disableAccess) {
      return;
    }
    this.marketplaceOpen = open;
    this.currencyOpen = false;
    if (open) {
      this.searchPanelOpen = false;
      this.requestEvents();
      if (link) {
        this.moveUnderline(link);
      }
    } else {
      this.underline = { ...this.underline, on: false };
    }
  }

  /** Asks the parent for events once; the parent caches them per network and group. */
  private requestEvents(): void {
    if (!this.eventsRequested) {
      this.eventsRequested = true;
      this.eventsNeeded.emit();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.setMarketplace(false);
      this.searchPanelOpen = false;
      return;
    }
    const target = event.target as HTMLElement;
    const typing = target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
    if (event.key === '/' && !typing && this.searchInput) {
      event.preventDefault();
      this.searchInput.nativeElement.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Element;
    if (!target?.closest?.('.tm-search')) {
      this.searchPanelOpen = false;
    }
    if (!this.host.nativeElement.contains(target)) {
      this.setMarketplace(false);
      this.currencyOpen = false;
    }
  }
}
